/**
 * Address Form Validation Schemas
 *
 * Zod schemas for address-related forms.
 * UI callers should prefer the `create*Schema` factories with next-intl messages.
 * Exported `*Schema` constants are English fallbacks for tests and non-UI callers.
 */

import { z } from 'zod';

const MAX_CHAR = 256;

/** i18n messages for address form schema factories */
export interface AddressFormSchemaMessages {
  requiredField: string;
  maxChars: (max: number) => string;
  invalidCountry: string;
  phoneTooLong: string;
  titleRequired: string;
  countryAreaRequired: string;
  abnRequired: string;
}

const ENGLISH_ADDRESS_MESSAGES: AddressFormSchemaMessages = {
  requiredField: 'This field is required.',
  maxChars: (max) => `${max} characters maximum.`,
  invalidCountry: 'Invalid country code.',
  phoneTooLong: 'Phone number too long.',
  titleRequired: 'Title is required',
  countryAreaRequired: 'State/Province is required.',
  abnRequired: 'ABN is required for Australian businesses.',
};

/**
 * Base address form schema with caller-supplied i18n messages.
 */
export function createAddressFormSchema(messages: AddressFormSchemaMessages) {
  const maxMsg = (max: number) => messages.maxChars(max);

  return z.object({
    // Empty string is what addressToFormData returns when user.personalTitle is missing.
    title: z.string().min(1, messages.titleRequired),
    firstName: z.string().min(1, messages.requiredField).max(MAX_CHAR, maxMsg(MAX_CHAR)),
    lastName: z.string().min(1, messages.requiredField).max(MAX_CHAR, maxMsg(MAX_CHAR)),
    companyName: z.string().max(MAX_CHAR, maxMsg(MAX_CHAR)).optional().nullable(),
    streetAddress1: z.string().min(1, messages.requiredField).max(MAX_CHAR, maxMsg(MAX_CHAR)),
    streetAddress2: z.string().max(MAX_CHAR, maxMsg(MAX_CHAR)).optional().nullable(),
    city: z.string().min(1, messages.requiredField).max(MAX_CHAR, maxMsg(MAX_CHAR)),
    postalCode: z.string().min(1, messages.requiredField).max(MAX_CHAR, maxMsg(MAX_CHAR)),
    countryArea: z.string().max(MAX_CHAR, maxMsg(MAX_CHAR)).optional().nullable(),
    country: z.string().min(2, messages.requiredField).max(2, messages.invalidCountry),
    phone: z.string().max(50, messages.phoneTooLong).optional().nullable(),
  });
}

/** English fallback for non-UI callers/tests */
export const addressFormSchema = createAddressFormSchema(ENGLISH_ADDRESS_MESSAGES);

export type AddressFormData = z.infer<typeof addressFormSchema>;

/**
 * Extended address schema with country-specific metadata fields.
 */
export function createExtendedAddressFormSchema(messages: AddressFormSchemaMessages) {
  const maxMsg = (max: number) => messages.maxChars(max);

  return createAddressFormSchema(messages)
    .extend({
      firstNameKanji: z.string().max(MAX_CHAR, maxMsg(MAX_CHAR)).optional().nullable(),
      lastNameKanji: z.string().max(MAX_CHAR, maxMsg(MAX_CHAR)).optional().nullable(),
      firstNameKatakana: z.string().max(MAX_CHAR, maxMsg(MAX_CHAR)).optional().nullable(),
      lastNameKatakana: z.string().max(MAX_CHAR, maxMsg(MAX_CHAR)).optional().nullable(),
      houseMansionName: z.string().max(MAX_CHAR, maxMsg(MAX_CHAR)).optional().nullable(),
      apartmentNumber: z.string().max(MAX_CHAR, maxMsg(MAX_CHAR)).optional().nullable(),
      vatNumber: z.string().max(MAX_CHAR, maxMsg(MAX_CHAR)).optional().nullable(),
      abn: z.string().max(MAX_CHAR, maxMsg(MAX_CHAR)).optional().nullable(),
      isDefaultShippingAddress: z.boolean().optional(),
      isDefaultBillingAddress: z.boolean().optional(),
    })
    .superRefine((data, ctx) => {
      if (data.country !== 'JP') return;
      if (!data.firstNameKanji?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: messages.requiredField,
          path: ['firstNameKanji'],
        });
      }
      if (!data.lastNameKanji?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: messages.requiredField,
          path: ['lastNameKanji'],
        });
      }
    });
}

/** English fallback for non-UI callers/tests */
export const extendedAddressFormSchema = createExtendedAddressFormSchema(ENGLISH_ADDRESS_MESSAGES);

export type ExtendedAddressFormData = z.infer<typeof extendedAddressFormSchema>;

export const VAT_COUNTRIES = ['GB'];
export const ABN_COUNTRIES = ['AU'];

/**
 * @deprecated Use Saleor addressValidationRules via useCountryAreaFieldConfig instead.
 */
export const COUNTRIES_REQUIRING_STATE = ['US', 'CA', 'AU', 'JP'];

/**
 * Country-aware address schema with conditional validation.
 */
export function createCountryAwareSchema(options: {
  countryCode: string;
  countryAreaRequired?: boolean;
  messages: AddressFormSchemaMessages;
}) {
  const { countryCode, countryAreaRequired = false, messages } = options;

  return createExtendedAddressFormSchema(messages).superRefine((data, ctx) => {
    if (countryAreaRequired && !data.countryArea?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: messages.countryAreaRequired,
        path: ['countryArea'],
      });
    }

    if (
      ABN_COUNTRIES.includes(countryCode) &&
      data.companyName &&
      data.companyName.trim() !== '' &&
      !data.abn
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: messages.abnRequired,
        path: ['abn'],
      });
    }
  });
}

export interface AddressApiRequest {
  addressId?: string;
  input: AddressFormData;
  isDefaultShipping?: boolean;
  isDefaultBilling?: boolean;
}

export interface AddressApiResponse {
  success: boolean;
  address?: {
    id: string;
    firstName: string;
    lastName: string;
    city: string;
    country: { code: string; country: string };
  };
  errors?: Array<{ field: string | null; message: string | null; code: string }>;
}
